/**
 * View-transition wrapper for the module-pane open/close flip.
 *
 * The browser's View Transitions API does exactly what a hand-rolled flight
 * surface can't: it SNAPSHOTS the real pixels of the chat panel (messages,
 * composer, module card — anything with a view-transition-name in
 * ChatBox.css), applies the layout flip underneath, and morphs the old
 * image into the new live render. The user keeps seeing their actual chat
 * content the whole way — no hidden content, no empty traveling card, no
 * fade-from-blank at landing.
 *
 * `animate` is decided by the caller (user-intent write + desktop split +
 * no reduced-motion — the same gates the old flight used); hydration /
 * conversation-switch / auto-cleanup writes never come through here.
 * Unsupported browsers (no startViewTransition) get the plain instant flip.
 *
 * flushSync: startViewTransition captures "new" after its callback resolves,
 * so the React commit (including cascading useLayoutEffect updates like the
 * docked-exit's expandChatPane) must flush synchronously inside it.
 *
 * Rapid toggles are safe: starting a new transition while one is animating
 * makes the browser skip the old one to its end state.
 */
import { flushSync } from 'react-dom';

/**
 * `transitionClass` (optional) is added to <html> for the duration of the
 * transition and removed when it finishes, so CSS can give a specific flip its
 * own choreography — e.g. `vt-pane-closing` slides the module pane out to the
 * left instead of the default morph. Inert on the instant (non-animated) path.
 */
export function flipPaneWithViewTransition(
  apply: () => void,
  animate: boolean,
  transitionClass?: string,
): void {
  // typeof guard doubles as the runtime feature check for browsers that
  // predate the API (lib.dom types it unconditionally).
  if (
    !animate
    || typeof document === 'undefined'
    || typeof document.startViewTransition !== 'function'
  ) {
    apply();
    return;
  }
  if (transitionClass) document.documentElement.classList.add(transitionClass);
  const transition = document.startViewTransition(() => {
    flushSync(apply);
  });
  if (transitionClass) {
    void transition.finished.finally(() => {
      document.documentElement.classList.remove(transitionClass);
    });
  }
}
